import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminFilesEditor() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [filterType, setFilterType] = useState("all");

    useEffect(() => {
        fetchFiles();
    }, []);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const res = await axios.get("/api/list-files", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFiles(res.data.files || []);
        } catch (error) {
            console.error("Error fetching files:", error);
            toast.error("Error fetching files");
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, HEIC, and PDF files are allowed');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading(true);

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'uploads');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                toast.success('File uploaded successfully!');
                fetchFiles();
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.success('URL copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy URL');
        });
    };

    const deleteFile = async (file) => {
        const ok = await confirmSwal({
            title: `Delete ${file.filename}?`,
            text: "This action cannot be undone.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.delete(`/api/delete-image?path=${encodeURIComponent(file.path)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('File deleted successfully!');
            fetchFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete file');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getFileType = (extension) => {
        const imageExts = ['.jpg', '.jpeg', '.png', '.heic'];
        const pdfExts = ['.pdf'];
        
        if (imageExts.includes(extension)) return 'image';
        if (pdfExts.includes(extension)) return 'pdf';
        return 'other';
    };

    const imageFiles = files.filter(f => getFileType(f.extension) === 'image');
    const pdfFiles = files.filter(f => getFileType(f.extension) === 'pdf');

    const filteredFiles = files.filter(file => {
        const fileType = getFileType(file.extension);
        if (filterType === 'images') return fileType === 'image';
        if (filterType === 'pdfs') return fileType === 'pdf';
        return true;
    });

    if (loading) return <p>Loading files...</p>;

    return (
        <div>
            <p className="text-body-secondary">
                Upload and manage images and PDFs. All files are stored in Digital Ocean Spaces.
            </p>

            {/* Upload Section */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Upload File</h5>
                    <div className="input-group">
                        <input 
                            type="file" 
                            className="form-control" 
                            accept=".jpg,.jpeg,.png,.heic,.pdf"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleFileUpload(file);
                                e.target.value = '';
                            }}
                            disabled={uploading}
                        />
                    </div>
                    <small className="text-muted d-block mt-2">
                        Supported: JPG, PNG, HEIC, PDF • Max 10MB
                    </small>
                </div>
            </div>

            {/* Filter Buttons */}
            <div className="btn-group mb-3" role="group">
                <button 
                    type="button" 
                    className={`btn ${filterType === 'all' ? 'btn-admin' : 'btn-secondary'}`}
                    onClick={() => setFilterType('all')}
                >
                    All ({files.length})
                </button>
                <button 
                    type="button" 
                    className={`btn ${filterType === 'images' ? 'btn-admin' : 'btn-secondary'}`}
                    onClick={() => setFilterType('images')}
                >
                    Images ({imageFiles.length})
                </button>
                <button 
                    type="button" 
                    className={`btn ${filterType === 'pdfs' ? 'btn-admin' : 'btn-secondary'}`}
                    onClick={() => setFilterType('pdfs')}
                >
                    PDFs ({pdfFiles.length})
                </button>
            </div>

            {/* Files List */}
            {filteredFiles.length === 0 ? (
                <div className="alert alert-info">
                    No files found. Upload a file to get started.
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>Type</th>
                                <th>Filename</th>
                                <th style={{ width: '120px' }}>Size</th>
                                <th style={{ width: '180px' }}>Uploaded</th>
                                <th style={{ width: '200px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredFiles.map((file, index) => {
                                const fileType = getFileType(file.extension);
                                return (
                                    <tr key={index}>
                                        <td>
                                            {fileType === 'image' ? (
                                                <span className="badge bg-success">IMG</span>
                                            ) : fileType === 'pdf' ? (
                                                <span className="badge bg-danger">PDF</span>
                                            ) : (
                                                <span className="badge bg-secondary">FILE</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {fileType === 'image' && (
                                                    <img 
                                                        src={file.url} 
                                                        alt={file.filename}
                                                        style={{ 
                                                            width: '40px', 
                                                            height: '40px', 
                                                            objectFit: 'cover',
                                                            marginRight: '10px',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                )}
                                                <div>
                                                    <div>{file.filename}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{formatFileSize(file.size)}</td>
                                        <td>
                                            <small>{formatDate(file.last_modified)}</small>
                                        </td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button 
                                                    className="btn btn-admin"
                                                    onClick={() => copyToClipboard(file.url)}
                                                    title="Copy URL"
                                                >
                                                    Copy URL
                                                </button>
                                                <a 
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-secondary"
                                                    title="View"
                                                >
                                                    View &#x2197;
                                                </a>
                                                <button 
                                                    className="btn btn-admin"
                                                    onClick={() => deleteFile(file)}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
